/**
 * useDashboardGroupsStore - Hook for dashboard group management with immediate API sync.
 *
 * All operations are immediately pushed to the server.
 * No local state - the server is the single source of truth.
 *
 * @deprecated Use useResourceGroupsStore with resourceType="dashboard" instead.
 */

import { createResourceHook } from "../use-resource-store";
import {
  useResourceGroups,
  useCreateResourceGroup,
  useUpdateResourceGroup,
  useDeleteResourceGroup,
} from "@/api";
import {
  ResourceGroup,
  ResourceGroupCreate,
} from "@/models/resource-group";

/**
 * Hook for dashboard group management with immediate API sync.
 *
 * @deprecated Use useResourceGroupsStore with resourceType="dashboard" instead.
 *
 * @example
 * ```tsx
 * function DashboardGroupList() {
 *   const {
 *     resources: groups,
 *     create,
 *     update,
 *     remove,
 *     isLoading,
 *     isUpdating,
 *   } = useDashboardGroupsStore();
 *
 *   const handleUpdate = async (id: string, name: string) => {
 *     await update(id, { name });
 *   };
 *
 *   return (
 *     <div>
 *       {groups.map(g => (
 *         <GroupItem key={g.id} group={g} onUpdate={handleUpdate} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useDashboardGroupsStore = createResourceHook<
  ResourceGroup,
  ResourceGroupCreate
>({
  resourceName: "resource-group",
  resourcePath: "resource_groups",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useResourceGroups("dashboard");
    return {
      data: data ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateResourceGroup();
    return {
      mutateAsync: async (data: ResourceGroupCreate) => {
        const result = await mutation.mutateAsync({
          ...data,
          resource_type: "dashboard",
        });
        return result as unknown as ResourceGroup;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateResourceGroup();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const { uriParams, ...rest } = data;
        const result = await mutation.mutateAsync({
          uriParams,
          ...rest,
        } as Parameters<typeof mutation.mutateAsync>[0]);
        return result as unknown as ResourceGroup;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteResourceGroup();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(data);
      },
    };
  },
});

export default useDashboardGroupsStore;
